import { useState } from "react";

import { useLang } from "../../lib/i18n";
import { mealsCopy } from "../../lib/i18n/meals";

type Props = {
  code: string;
  invites: number | null;
};

function shareUrl(code: string): string {
  if (typeof window === "undefined") return `/r/${code}`;
  return `${window.location.origin}/r/${code}`;
}

export function InviteChip({ code, invites }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const url = shareUrl(code);
    try {
      if (navigator.share) {
        // On phones (most users) the native share sheet beats a clipboard
        // copy: opens iMessage / Signal / Insta DM directly. Falls back if
        // the user dismisses it without sharing.
        await navigator.share({
          title: "Sytofood",
          text: t.inviteShareText,
          url,
        });
        setCopied(true);
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch {
      // Either user cancelled the share sheet or clipboard API is blocked
      // (rare in modern browsers but possible in iframes). Best-effort
      // fallback: try clipboard once more, swallow on failure.
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      } catch {
        // give up
      }
    }
    if (copied) return;
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="w-full flex items-center justify-between gap-3 transition-colors"
      style={{
        padding: "10px 14px",
        borderRadius: 12,
        background: "var(--surface)",
        boxShadow: "inset 0 0 0 1px var(--nav-border)",
        textAlign: "left",
      }}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            letterSpacing: "0.12em",
            color: "var(--muted)",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {t.inviteCodeLabel}
        </span>
        <span
          className="tabular truncate"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            fontWeight: 700,
            color: "var(--ink)",
            letterSpacing: "0.04em",
          }}
        >
          {code}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {invites !== null ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: invites > 0 ? "var(--accent)" : "var(--muted)",
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          >
            {invites} {t.inviteCount(invites)}
          </span>
        ) : null}
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: copied ? "var(--ok-fg)" : "var(--accent)",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {copied ? t.inviteCopied : t.inviteShare}
        </span>
      </div>
    </button>
  );
}
